import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  fetchPlayerMatches,
  fetchPlayerRatingHistory,
  type MatchEntry,
  type RankingItem,
  type RatingHistoryEntry,
} from "../utils/homeApi";
import { determineMatchResult } from "../utils/matchResultHelper";
import { buildRatingChartPoints } from "../utils/ratingChartPoints";
import { byNewestFirst, byOldestFirst } from "../utils/match-order";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface OpponentStats {
  matches: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
  opponentId: number | null;
  opponentName: string;
}

function getReversedMatchResult(matchResult: string): string {
  if (!matchResult) {
    return "N/A";
  }

  if (matchResult.includes("-")) {
    const parts = matchResult.split("-");
    if (parts.length === 2) {
      return `${parts[1]}-${parts[0]}`;
    }
  }

  return matchResult;
}

function getReversedSetScores(setScores: string[]): string[] {
  if (!setScores || setScores.length === 0) {
    return [];
  }

  return setScores.map((score) => {
    if (score.includes(":")) {
      const parts = score.split(":");
      if (parts.length === 2) {
        return `${parts[1]}:${parts[0]}`;
      }
    }

    return score;
  });
}

function getRatingBadgeClass(rating: number): string {
  if (rating < 1600) {
    return "low";
  }

  if (rating < 1800) {
    return "medium";
  }

  if (rating < 2000) {
    return "high";
  }

  return "very-high";
}

interface Props {
  playerId: number;
  singles: RankingItem;
}

const SinglesView: React.FC<Props> = ({ playerId, singles }) => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState<MatchEntry[]>([]);
  const [ratingHistoryRaw, setRatingHistoryRaw] = useState<RatingHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState<"overview" | "matches" | "opponents">("overview");
  const [expandedOpponents, setExpandedOpponents] = useState<Set<string>>(new Set());
  const [chartXAxis, setChartXAxis] = useState<"tournament" | "date">("tournament");

  useEffect(() => {
    let alive = true;
    fetchPlayerRatingHistory(playerId, "SINGLES")
      .then((res) => {
        if (alive) {
          setRatingHistoryRaw(res.items);
        }
      })
      .catch((error) => {
        console.error("Error loading singles rating history:", error);
      });

    return () => {
      alive = false;
    };
  }, [playerId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPlayerMatches(playerId, { limit: 2000, kind: "singles" })
      .then((res) => {
        if (alive) {
          setMatches(res.items);
        }
      })
      .catch((error) => {
        console.error("Error loading singles matches:", error);
      })
      .finally(() => {
        if (alive) {
          setLoading(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [playerId]);

  const opponents: Record<string, OpponentStats> = {};
  let totalMatches = 0;

  matches.forEach((match) => {
    const isPlayer1 = match.player1Id === playerId;
    const isPlayer2 = match.player2Id === playerId;
    if (!isPlayer1 && !isPlayer2) {
      return;
    }

    totalMatches++;

    const opponentName = (isPlayer1 ? match.player2 : match.player1) ?? "Nieznany";
    const opponentId = isPlayer1 ? match.player2Id ?? null : match.player1Id ?? null;
    const key = opponentName;

    if (!opponents[key]) {
      opponents[key] = {
        matches: 0,
        wins: 0,
        losses: 0,
        draws: 0,
        winRate: 0,
        opponentId,
        opponentName,
      };
    }

    opponents[key].matches++;

    const result = determineMatchResult(match as never, isPlayer1);
    if (result === "W") {
      opponents[key].wins++;
    } else if (result === "L") {
      opponents[key].losses++;
    } else {
      opponents[key].draws++;
    }
  });

  Object.values(opponents).forEach((opponent) => {
    opponent.winRate = opponent.matches > 0 ? (opponent.wins / opponent.matches) * 100 : 0;
  });

  // Match-side fallback: align chronologically with history when the API
  // can't resolve tournamentNumber from a matchId-less backfill snapshot.
  const sortedMatchesForChart = matches
    .slice()
    .sort(byOldestFirst);

  // API already returns snapshots in ELO compute order (file-num chronology).
  // Re-sorting by playedAt would scramble them — header dates are unreliable.
  const ratingHistory = buildRatingChartPoints(
    ratingHistoryRaw,
    (idx) => sortedMatchesForChart[idx]?.tournamentNumber ?? null,
  );

  const formatChartDate = (ts: number) => {
    const d = new Date(ts);
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getFullYear()).slice(2)}`;
  };

  const sparseTicksByIndex = (count: number, dataLen: number): number[] => {
    if (dataLen === 0) {
      return [];
    }
    const step = Math.max(1, Math.ceil(dataLen / count));
    const out: number[] = [];
    for (let i = 0; i < dataLen; i += step) {
      out.push(i + 1);
    }
    if (out[out.length - 1] !== dataLen) {
      out.push(dataLen);
    }
    return out;
  };

  const dateTicks: number[] = (() => {
    if (ratingHistory.length === 0) {
      return [];
    }
    const targetTicks = 8;
    const step = Math.max(1, Math.ceil(ratingHistory.length / targetTicks));
    const out: number[] = [];
    for (let i = 0; i < ratingHistory.length; i += step) {
      out.push(ratingHistory[i].playedAtTs);
    }
    const lastTs = ratingHistory[ratingHistory.length - 1].playedAtTs;
    if (out[out.length - 1] !== lastTs) {
      out.push(lastTs);
    }
    return out;
  })();

  const tournamentTicks: number[] = sparseTicksByIndex(8, ratingHistory.length);

  const tournamentTickFormatter = (idx: number) => {
    const entry = ratingHistory[Math.max(0, Math.min(ratingHistory.length - 1, idx - 1))];
    const tn = entry?.tournamentNumber;
    return typeof tn === "number" ? `#${tn}` : `${idx}`;
  };

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Aktualny rating (singiel)</h3>
          <p className="value">
            <span className={`rating-badge ${getRatingBadgeClass(singles.rating)}`}>
              {singles.rating}
            </span>
          </p>
        </div>
        <div className="stat-card">
          <h3>Łącznie meczów</h3>
          <p className="value">{singles.gamesPlayed}</p>
        </div>
        <div className="stat-card">
          <h3>% Wygranych</h3>
          <p
            className="value"
            title={
              singles.gamesPlayed > 0
                ? `Wygrane ${((singles.gamesWon / singles.gamesPlayed) * 100).toFixed(1)}% · Remisy ${((singles.gamesDrawn / singles.gamesPlayed) * 100).toFixed(1)}% · Przegrane ${((singles.gamesLost / singles.gamesPlayed) * 100).toFixed(1)}%`
                : "Brak meczów"
            }
          >
            {singles.gamesPlayed > 0
              ? `${((singles.gamesWon / singles.gamesPlayed) * 100).toFixed(1)}%`
              : "—"}
          </p>
        </div>
        <div className="stat-card">
          <h3>Turnieje</h3>
          <p className="value">{singles.tournamentsPlayed}</p>
        </div>
      </div>

      <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
        <button
          onClick={() => setSubTab("overview")}
          className={subTab === "overview" ? "btn-primary" : "btn-secondary"}
        >
          Przegląd
        </button>
        <button
          onClick={() => setSubTab("matches")}
          className={subTab === "matches" ? "btn-primary" : "btn-secondary"}
        >
          Historia meczów{loading ? " …" : ` (${totalMatches})`}
        </button>
        <button
          onClick={() => setSubTab("opponents")}
          className={subTab === "opponents" ? "btn-primary" : "btn-secondary"}
        >
          Przeciwnicy{loading ? "" : ` (${Object.keys(opponents).length})`}
        </button>
      </div>

      {subTab === "overview" && (
        <div>
          <h3>Statystyki singla</h3>
          <div className="table-container">
            <table>
              <tbody>
                <tr>
                  <td>
                    <strong>Początkowy rating:</strong>
                  </td>
                  <td>{singles.startingRating ?? "—"}</td>
                  <td>
                    <strong>Aktualny rating:</strong>
                  </td>
                  <td>{singles.rating}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Zmiana ratingu w ostatnim turnieju:</strong>
                  </td>
                  <td style={{ color: singles.ratingDifference >= 0 ? "green" : "red" }}>
                    {singles.ratingDifference >= 0 ? "+" : ""}
                    {singles.ratingDifference}
                  </td>
                  <td>
                    <strong>Rozegrane mecze:</strong>
                  </td>
                  <td>{singles.gamesPlayed}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Wygrane:</strong>
                  </td>
                  <td>{singles.gamesWon}</td>
                  <td>
                    <strong>Przegrane:</strong>
                  </td>
                  <td>{singles.gamesLost}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Remisy:</strong>
                  </td>
                  <td>{singles.gamesDrawn}</td>
                  <td>
                    <strong>% Wygranych:</strong>
                  </td>
                  <td
                    title={
                      singles.gamesPlayed > 0
                        ? `Wygrane ${((singles.gamesWon / singles.gamesPlayed) * 100).toFixed(1)}% · Remisy ${((singles.gamesDrawn / singles.gamesPlayed) * 100).toFixed(1)}% · Przegrane ${((singles.gamesLost / singles.gamesPlayed) * 100).toFixed(1)}%`
                        : "Brak meczów"
                    }
                  >
                    {singles.gamesPlayed > 0
                      ? `${((singles.gamesWon / singles.gamesPlayed) * 100).toFixed(1)}%`
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Pierwszy turniej:</strong>
                  </td>
                  <td>
                    {singles.firstTournament != null ? (
                      <Link to={`/tournament/${singles.firstTournament}`} className="player-link">
                        #{singles.firstTournament}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <strong>Ostatni turniej:</strong>
                  </td>
                  <td>
                    {singles.latestTournament != null ? (
                      <Link to={`/tournament/${singles.latestTournament}`} className="player-link">
                        #{singles.latestTournament}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {ratingHistory.length > 0 && (
            <div style={{ marginTop: "30px" }}>
              <h3>
                Historia ratingu (singiel){" "}
                <span style={{ color: "#666", fontWeight: 400, fontSize: "0.85rem" }}>
                  · obecnie {singles.rating}
                </span>
              </h3>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <button
                  onClick={() => setChartXAxis("tournament")}
                  className={chartXAxis === "tournament" ? "btn-primary" : "btn-secondary"}
                  style={{ padding: "4px 10px", fontSize: "0.85rem" }}
                >
                  Numer turnieju
                </button>
                <button
                  onClick={() => setChartXAxis("date")}
                  className={chartXAxis === "date" ? "btn-primary" : "btn-secondary"}
                  style={{ padding: "4px 10px", fontSize: "0.85rem" }}
                >
                  Data
                </button>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={ratingHistory} margin={{ top: 5, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  {chartXAxis === "date" ? (
                    <XAxis
                      dataKey="playedAtTs"
                      type="number"
                      scale="time"
                      domain={["dataMin", "dataMax"]}
                      ticks={dateTicks}
                      tickFormatter={formatChartDate}
                      label={{ value: "Data", position: "insideBottom", offset: -10 }}
                    />
                  ) : (
                    <XAxis
                      dataKey="index"
                      type="number"
                      domain={[1, ratingHistory.length || 1]}
                      ticks={tournamentTicks}
                      tickFormatter={tournamentTickFormatter}
                      label={{ value: "Numer turnieju", position: "insideBottom", offset: -10 }}
                    />
                  )}
                  <YAxis
                    label={{ value: "Rating", angle: -90, position: "insideLeft" }}
                    domain={["dataMin - 50", "dataMax + 50"]}
                  />
                  <Tooltip
                    labelFormatter={(value) =>
                      chartXAxis === "date"
                        ? formatChartDate(Number(value))
                        : tournamentTickFormatter(Number(value))
                    }
                    formatter={(value) => [String(value ?? ""), "Rating"]}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="rating"
                    stroke="#8884d8"
                    name="Rating"
                    dot={{
                      r: 3,
                      cursor: "pointer",
                      onClick: (_event: unknown, payload: unknown) => {
                        const tn = (payload as { payload?: { tournamentNumber?: number | null } })
                          ?.payload?.tournamentNumber;
                        if (typeof tn === "number") {
                          navigate(`/tournament/${tn}`);
                        }
                      },
                    }}
                    activeDot={{
                      r: 6,
                      cursor: "pointer",
                      onClick: (_event: unknown, payload: unknown) => {
                        const tn = (payload as { payload?: { tournamentNumber?: number | null } })
                          ?.payload?.tournamentNumber;
                        if (typeof tn === "number") {
                          navigate(`/tournament/${tn}`);
                        }
                      },
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {subTab === "matches" && (
        <div>
          <h3>Ostatnie mecze (singiel)</h3>
          {loading ? (
            <p>Wczytywanie meczów…</p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Turniej</th>
                    <th>Przeciwnik</th>
                    <th>Rating przeciwnika</th>
                    <th>Wyniki setów</th>
                    <th>Wartość wyniku</th>
                    <th>Oczekiwany wynik</th>
                    <th>Rating przed</th>
                    <th>Rating po</th>
                    <th>Zmiana ratingu</th>
                  </tr>
                </thead>
                <tbody>
                  {matches
                    .slice()
                    .sort(byNewestFirst)
                    .map((match, index) => {
                      const isPlayer1 = match.player1Id === playerId;
                      const opponentName = (isPlayer1 ? match.player2 : match.player1) ?? "Nieznany";
                      const opponentId = isPlayer1 ? match.player2Id : match.player1Id;
                      const ratingBefore = isPlayer1
                        ? match.player1RatingBefore || 0
                        : match.player2RatingBefore || 0;
                      const ratingAfter = isPlayer1
                        ? match.player1RatingAfter || 0
                        : match.player2RatingAfter || 0;
                      const ratingChange = isPlayer1
                        ? match.player1RatingChange || 0
                        : match.player2RatingChange || 0;

                      const displayScore = isPlayer1
                        ? match.score ?? 0
                        : 1 - (match.score ?? 0);
                      const displayMatchResult = isPlayer1
                        ? match.matchResult || "N/A"
                        : getReversedMatchResult(match.matchResult || "");
                      const displaySetScores = isPlayer1
                        ? match.setScores
                        : getReversedSetScores(match.setScores || []);

                      const opponentRatingBefore = isPlayer1
                        ? match.player2RatingBefore
                        : match.player1RatingBefore;

                      const expectedScore = isPlayer1
                        ? (match as unknown as { expectedScore1?: number }).expectedScore1
                        : (match as unknown as { expectedScore2?: number }).expectedScore2;

                      return (
                        <tr key={index}>
                          <td>
                            <Link to={`/tournament/${match.tournamentNumber}`} className="player-link">
                              #{match.tournamentNumber}
                            </Link>
                          </td>
                          <td>
                            {opponentId != null ? (
                              <Link to={`/player/${opponentId}`} className="player-link">
                                {opponentName}
                              </Link>
                            ) : (
                              <span>{opponentName}</span>
                            )}
                          </td>
                          <td>{opponentRatingBefore || "N/A"}</td>
                          <td>
                            {displaySetScores && displaySetScores.length > 0 ? (
                              <div>
                                <div style={{ fontWeight: "bold", marginBottom: "0.25rem" }}>
                                  {displayMatchResult}
                                </div>
                                <div style={{ fontSize: "0.8rem", color: "#666" }}>
                                  {displaySetScores.join(" ")}
                                </div>
                              </div>
                            ) : (
                              <span style={{ color: "#999", fontStyle: "italic" }}>
                                Brak wyników setów
                              </span>
                            )}
                          </td>
                          <td>
                            <span
                              style={{
                                padding: "0.25rem 0.5rem",
                                borderRadius: "15px",
                                background: "#6c757d",
                                color: "white",
                                fontSize: "0.9rem",
                                fontFamily: "monospace",
                              }}
                            >
                              {displayScore.toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <span
                              style={{
                                padding: "0.25rem 0.5rem",
                                borderRadius: "15px",
                                background: "#17a2b8",
                                color: "white",
                                fontSize: "0.9rem",
                                fontFamily: "monospace",
                              }}
                            >
                              {expectedScore != null ? expectedScore.toFixed(2) : "N/A"}
                            </span>
                          </td>
                          <td>{ratingBefore}</td>
                          <td>{ratingAfter}</td>
                          <td style={{ color: ratingChange >= 0 ? "green" : "red" }}>
                            {ratingChange >= 0 ? "+" : ""}
                            {ratingChange}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {subTab === "opponents" && (
        <div>
          <h3>Statystyki z przeciwnikami</h3>
          {loading ? (
            <p>Wczytywanie meczów…</p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Przeciwnik</th>
                    <th>Mecze</th>
                    <th>Wygrane</th>
                    <th>Przegrane</th>
                    <th>Remisy</th>
                    <th title="Procent wygranych spotkań. Najedź na wartość żeby zobaczyć remisy/przegrane.">
                      % Wygranych
                    </th>
                    <th>Szczegóły</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(opponents)
                    .sort(([, a], [, b]) => b.matches - a.matches)
                    .map(([opponentName, stats]) => {
                      const opponentMatches = matches.filter((m) => {
                        const isPlayer1 = m.player1Id === playerId;

                        return (
                          (isPlayer1 && m.player2 === opponentName) ||
                          (!isPlayer1 && m.player1 === opponentName)
                        );
                      });
                      const isExpanded = expandedOpponents.has(opponentName);

                      return (
                        <React.Fragment key={opponentName}>
                          <tr>
                            <td>
                              {stats.opponentId != null ? (
                                <Link to={`/player/${stats.opponentId}`} className="player-link">
                                  {opponentName}
                                </Link>
                              ) : (
                                <span>{opponentName}</span>
                              )}
                            </td>
                            <td>{stats.matches}</td>
                            <td>{stats.wins}</td>
                            <td>{stats.losses}</td>
                            <td>{stats.draws}</td>
                            <td
                              title={
                                stats.matches > 0
                                  ? `Wygrane ${((stats.wins / stats.matches) * 100).toFixed(1)}% · Remisy ${((stats.draws / stats.matches) * 100).toFixed(1)}% · Przegrane ${((stats.losses / stats.matches) * 100).toFixed(1)}%`
                                  : "Brak meczów"
                              }
                            >
                              {stats.matches > 0
                                ? `${((stats.wins / stats.matches) * 100).toFixed(1)}%`
                                : "—"}
                            </td>
                            <td>
                              <button
                                onClick={() => {
                                  const newExpanded = new Set(expandedOpponents);
                                  if (isExpanded) {
                                    newExpanded.delete(opponentName);
                                  } else {
                                    newExpanded.add(opponentName);
                                  }

                                  setExpandedOpponents(newExpanded);
                                }}
                                className="btn-secondary"
                                style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem" }}
                              >
                                {isExpanded ? "Ukryj" : "Pokaż"} szczegóły
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={8}>
                                <div
                                  style={{
                                    padding: "1rem",
                                    backgroundColor: "#f8f9fa",
                                    borderTop: "1px solid #dee2e6",
                                  }}
                                >
                                  <h4 style={{ margin: "0 0 1rem 0", color: "#495057" }}>
                                    Szczegóły meczów z {opponentName}
                                  </h4>
                                  <div className="table-container">
                                    <table style={{ fontSize: "0.9rem" }}>
                                      <thead>
                                        <tr>
                                          <th>Turniej</th>
                                          <th>Rezultat</th>
                                          <th>Rating przeciwnika</th>
                                          <th>Rating przed</th>
                                          <th>Rating po</th>
                                          <th>Zmiana ratingu</th>
                                          <th>Wyniki setów</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {opponentMatches
                                          .slice()
                                          .sort(byNewestFirst)
                                          .map((match, index) => {
                                            const isPlayer1 = match.player1Id === playerId;
                                            const ratingBefore = isPlayer1
                                              ? match.player1RatingBefore
                                              : match.player2RatingBefore;
                                            const ratingAfter = isPlayer1
                                              ? match.player1RatingAfter
                                              : match.player2RatingAfter;
                                            const ratingChange = isPlayer1
                                              ? match.player1RatingChange
                                              : match.player2RatingChange;
                                            const result = determineMatchResult(
                                              match as never,
                                              isPlayer1,
                                            );
                                            const displayMatchResult = isPlayer1
                                              ? match.matchResult || "N/A"
                                              : getReversedMatchResult(match.matchResult || "");
                                            const displaySetScores = isPlayer1
                                              ? match.setScores
                                              : getReversedSetScores(match.setScores || []);
                                            const opponentRatingBefore = isPlayer1
                                              ? match.player2RatingBefore
                                              : match.player1RatingBefore;

                                            return (
                                              <tr key={index}>
                                                <td>
                                                  <Link
                                                    to={`/tournament/${match.tournamentNumber}`}
                                                    className="player-link"
                                                  >
                                                    {match.tournamentNumber}
                                                  </Link>
                                                </td>
                                                <td>
                                                  <span
                                                    style={{
                                                      fontWeight: "bold",
                                                      color: result === "W" ? "green" : "red",
                                                    }}
                                                  >
                                                    {result}
                                                  </span>
                                                </td>
                                                <td>{opponentRatingBefore || "N/A"}</td>
                                                <td>{ratingBefore || "N/A"}</td>
                                                <td>{ratingAfter || "N/A"}</td>
                                                <td
                                                  style={{
                                                    color: (ratingChange || 0) >= 0 ? "green" : "red",
                                                  }}
                                                >
                                                  {(ratingChange || 0) >= 0 ? "+" : ""}
                                                  {ratingChange || "N/A"}
                                                </td>
                                                <td>
                                                  {displaySetScores && displaySetScores.length > 0 ? (
                                                    <div>
                                                      <div
                                                        style={{
                                                          fontWeight: "bold",
                                                          marginBottom: "0.25rem",
                                                        }}
                                                      >
                                                        {displayMatchResult}
                                                      </div>
                                                      <div
                                                        style={{
                                                          fontSize: "0.8rem",
                                                          color: "#666",
                                                        }}
                                                      >
                                                        {displaySetScores.join(" ")}
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    <span
                                                      style={{ color: "#999", fontStyle: "italic" }}
                                                    >
                                                      Brak wyników setów
                                                    </span>
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SinglesView;
